// NivelUsuariosPage.tsx — Co Marca Admin — estilo CategoryPage
import Loading from '../../components/Loading';
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Edit, Trash2, Shield, Plus, ChevronRight, Home } from 'lucide-react';
import UserLevelModal from '../../components/Userslevel/UserLevelModal';
import EditUserLevelModal from '../../components/Userslevel/EditUserLevelModal';
import DeleteConfirmationModal from '../../components/Userslevel/DeleteConfirmationModal';
import Pagination from '../../components/Pagination/Pagination';
import ExpiredSessionModal from '../../components/Expired/ExpiredSessionModal';
import { fetchUserLevels } from '../../services/userLevelService';
import { useSessionExpiration } from '../../hooks/useSessionExpiration';
import { useAuth } from '../../context/AuthContext';
import { useModelPermission } from '../../hooks/useModelPermission';
import { usePermissions } from '../../context/PermissionsContext';
import { canAccessRoute } from '../../utils/routeAccess';

const colors = {
    primary:        '#c1348b',
    primaryDark:    '#8f1f64',
    secondary:      '#61257b',
    secondaryLight: '#8b3fa8',
    accent:         '#ffd97c',
    accentDark:     '#ea8406',
};

const css = `
@import url('https://fonts.googleapis.com/css2?family=DM+Sans:wght@300;400;500;600;700;800;900&display=swap');
.nup-page { font-family:'DM Sans',sans-serif; }
@keyframes nupFadeUp { from{opacity:0;transform:translateY(18px);}to{opacity:1;transform:translateY(0);} }
.nup-fadeup   { animation:nupFadeUp .5s cubic-bezier(.16,1,.3,1) both; }
.nup-fadeup-1 { animation:nupFadeUp .5s .08s cubic-bezier(.16,1,.3,1) both; }
.nup-btn-primary {
    display:inline-flex;align-items:center;gap:7px;
    background:linear-gradient(135deg,${colors.primary},${colors.primaryDark});
    color:#fff;border:none;border-radius:11px;
    padding:9px 20px;font-size:.83rem;font-weight:800;
    cursor:pointer;font-family:'DM Sans',sans-serif;
    box-shadow:0 4px 14px rgba(193, 52, 139,.25);
    transition:all .22s cubic-bezier(.16,1,.3,1);white-space:nowrap;
}
.nup-btn-primary:hover{transform:translateY(-2px);box-shadow:0 8px 22px rgba(193, 52, 139,.32);}
.nup-table-row{transition:background .15s;}
.nup-table-row:hover{background:#fafcff;}
.nup-action-btn{width:30px;height:30px;border-radius:9px;border:none;display:inline-flex;align-items:center;justify-content:center;cursor:pointer;transition:all .2s;}
.custom-scroll::-webkit-scrollbar{height:6px;width:6px;}
.custom-scroll::-webkit-scrollbar-track{background:transparent;}
.custom-scroll::-webkit-scrollbar-thumb{background-color:rgba(97, 37, 123,0.3);border-radius:20px;}
.custom-scroll::-webkit-scrollbar-thumb:hover{background-color:rgba(97, 37, 123,0.5);}
`;

const NivelUsuariosPage: React.FC = () => {
    const { isSessionExpired, setIsSessionExpired } = useSessionExpiration();
    const { user } = useAuth();
    const userLevelId = user?.user_level_id ?? 0;
    const perm = useModelPermission('userlevel', userLevelId);
    const permissionsModel = useModelPermission('modelpermission', userLevelId);
    const { permissions, disabledPaths } = usePermissions();
    const administrator = user?.role === 'Administrador';
    const navigate = useNavigate();

    const [isModalOpen, setIsModalOpen]         = useState(false);
    const [userLevels, setUserLevels]           = useState<any[]>([]);
    const [loadError, setLoadError] = useState<string | null>(null);
    const [currentPage, setCurrentPage]         = useState(1);
    const itemsPerPage = 10;
    const [editModalOpen, setEditModalOpen]     = useState(false);
    const [selectedLevel, setSelectedLevel]     = useState<any>(null);
    const [deleteModalOpen, setDeleteModalOpen] = useState(false);
    const [levelToDelete, setLevelToDelete]     = useState<any>(null);

    const loadUserLevels = useCallback(async (newLevels?: any) => {
        if (!perm?.can_read) return;
        setLoadError(null);
        try {
            if (newLevels) setUserLevels(newLevels.sort((a: any, b: any) => a.id - b.id));
            else { const data = await fetchUserLevels(); setUserLevels(data.sort((a: any, b: any) => a.id - b.id)); }
        } catch (e: any) {
            setUserLevels([]);
            setLoadError(e?.response?.status === 403 ? 'Tu nivel no tiene autorización para consultar los niveles de usuario.' : 'No se pudieron cargar los niveles de usuario. Vuelve a intentarlo.');
        }
    }, [perm]);

    useEffect(() => { if (perm) loadUserLevels(); }, [perm, loadUserLevels]);

    const sortedLevels    = useMemo(() => [...userLevels].sort((a,b)=>a.id-b.id), [userLevels]);
    const totalPages      = Math.ceil(sortedLevels.length / itemsPerPage);
    const displayedLevels = sortedLevels.slice((currentPage-1)*itemsPerPage, currentPage*itemsPerPage);

    if (perm === null) return <Loading text="Cargando niveles de usuarios..." />;

    return (
        <>
            <style>{css}</style>
            <div className="nup-page" style={{ background:'#fff', minHeight:'100vh', padding:'clamp(24px,3vw,36px) clamp(20px,4vw,40px)' }}>

                {/* ── HEADER ── */}
                <div className="nup-fadeup" style={{ marginBottom:'28px' }}>
                    <nav style={{ display:'flex', alignItems:'center', gap:'6px', fontSize:'.72rem', fontWeight:'600', color:'#94a3b8', marginBottom:'12px' }}>
                        <a href="/dashboard" style={{ color:'#94a3b8', textDecoration:'none', display:'flex', alignItems:'center', gap:'3px', transition:'color .2s' }}
                            onMouseEnter={e=>e.currentTarget.style.color=colors.primary}
                            onMouseLeave={e=>e.currentTarget.style.color='#94a3b8'}>
                            <Home size={11}/> Dashboard
                        </a>
                        <ChevronRight size={10} color="#e2e8f0"/>
                        <span style={{ color:colors.primary, fontWeight:'700' }}>Niveles de Usuario</span>
                    </nav>
                    <div style={{ display:'flex', alignItems:'flex-start', justifyContent:'space-between', flexWrap:'wrap', gap:'16px' }}>
                        <div>
                            <h1 style={{ fontSize:'clamp(1.5rem,3vw,2rem)', fontWeight:'900', color:'#0f172a', letterSpacing:'-.025em', lineHeight:1.1, marginBottom:'6px' }}>
                                Niveles de <span style={{ color:colors.primary }}>Usuario</span>
                            </h1>
                            <p style={{ fontSize:'.85rem', color:'#64748b', fontWeight:'500' }}>Administra los niveles y perfiles de acceso del sistema</p>
                            <div style={{ display:'flex', gap:'5px', marginTop:'10px' }}>
                                {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                    <div key={i} style={{ height:'3px', width:i===0?'24px':'8px', borderRadius:'3px', background:c }}/>
                                ))}
                            </div>
                        </div>
                        {administrator && perm.can_create && (
                            <button className="nup-btn-primary" onClick={()=>setIsModalOpen(true)}>
                                <Plus size={15}/> Agregar Nivel
                            </button>
                        )}
                    </div>
                </div>

                {loadError && <div role="alert" style={{ padding: '14px', marginBottom: '16px', borderRadius: '12px', background: '#fff1f2', color: '#9f1239' }}>{loadError}</div>}

                {/* ── TABLA ── */}
                <div className="nup-fadeup-1" style={{ background:'#fff', borderRadius:'20px', border:'1.5px solid #f1f5f9', boxShadow:'0 4px 24px rgba(0,0,0,.04)', overflow:'hidden' }}>

                    <ExpiredSessionModal isOpen={isSessionExpired} onClose={()=>{ setIsSessionExpired(false); window.location.href='/login'; }}/>
                    <UserLevelModal isOpen={isModalOpen} onClose={()=>setIsModalOpen(false)} addUserLevel={l=>setUserLevels(p=>[...p,l].sort((a,b)=>a.id-b.id))}/>
                    <EditUserLevelModal isOpen={editModalOpen} onClose={()=>setEditModalOpen(false)} userLevel={selectedLevel} onSave={()=>{ loadUserLevels(); setEditModalOpen(false); }}/>
                    <DeleteConfirmationModal isOpen={deleteModalOpen} onClose={()=>setDeleteModalOpen(false)} user={levelToDelete} onDeleted={()=>{ loadUserLevels(); setDeleteModalOpen(false); }}/>

                    {/* Contador */}
                    <div style={{ padding:'16px 24px', borderBottom:'1.5px solid #f1f5f9', display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                        <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                            <div style={{ width:'32px', height:'32px', borderRadius:'10px', background:`${colors.primary}12`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                <Shield size={15} color={colors.primary}/>
                            </div>
                            <span style={{ fontSize:'.82rem', fontWeight:'800', color:'#0f172a' }}>{sortedLevels.length}</span>
                            <span style={{ fontSize:'.78rem', color:'#94a3b8', fontWeight:'500' }}>niveles registrados</span>
                        </div>
                        <span style={{ fontSize:'.72rem', color:'#94a3b8', fontWeight:'600' }}>Página {currentPage} de {totalPages||1}</span>
                    </div>

                    {!perm.can_read ? (
                        <div style={{ padding:'60px 24px', textAlign:'center', color:'#94a3b8' }}>
                            <Shield size={40} style={{ margin:'0 auto 12px', opacity:.3 }}/>
                            <p style={{ fontWeight:'700' }}>No tienes permiso para ver niveles de usuario.</p>
                        </div>
                    ) : (
                        <>
                            <div className="custom-scroll" style={{ overflowX:'auto' }}>
                                <table style={{ width:'100%', borderCollapse:'collapse', fontFamily:"'DM Sans',sans-serif" }}>
                                    <thead>
                                        <tr style={{ background:'#fafcff', borderBottom:'1.5px solid #f1f5f9' }}>
                                            {['Nombre','Perfil Asociado','Fecha de creación','Estado','Acciones'].map((h,i)=>(
                                                <th key={i} style={{ padding:'12px 18px', textAlign:'left', fontSize:'.68rem', fontWeight:'800', color:'#94a3b8', textTransform:'uppercase', letterSpacing:'.08em', whiteSpace:'nowrap' }}>
                                                    {h}
                                                </th>
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {displayedLevels.map(level=>(
                                            <tr key={level.id} className="nup-table-row" style={{ borderBottom:'1px solid #f8fafc' }}>
                                                {/* Nombre */}
                                                <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                                    <span style={{ fontSize:'.85rem', fontWeight:'700', color:'#0f172a' }}>{level.nombre}</span>
                                                </td>
                                                {/* Perfil */}
                                                <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                                    <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background: level.perfil_asociado==='cliente'?`${colors.secondary}10`:`${colors.accentDark}10`, border:`1px solid ${level.perfil_asociado==='cliente'?colors.secondary:colors.accentDark}22`, color:level.perfil_asociado==='cliente'?colors.secondary:colors.accentDark, borderRadius:'6px', padding:'2px 8px', fontSize:'.72rem', fontWeight:'700' }}>
                                                        {level.perfil_asociado==='cliente'?'Cliente':'Personal'}
                                                    </span>
                                                </td>
                                                {/* Fecha */}
                                                <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                                    <span style={{ fontSize:'.78rem', color:'#64748b', fontWeight:'500' }}>
                                                        {new Date(level.fecha_creacion).toLocaleDateString('es-ES',{day:'2-digit',month:'2-digit',year:'numeric'})}
                                                    </span>
                                                </td>
                                                {/* Estado */}
                                                <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                                    {level.activo ? (
                                                        <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:`${colors.secondaryLight}14`, border:`1px solid ${colors.secondaryLight}30`, color:'#065f46', borderRadius:'100px', padding:'3px 10px', fontSize:'.7rem', fontWeight:'700' }}>
                                                            <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:colors.secondaryLight, display:'inline-block' }}/>Activo
                                                        </span>
                                                    ) : (
                                                        <span style={{ display:'inline-flex', alignItems:'center', gap:'5px', background:`${colors.primary}10`, border:`1px solid ${colors.primary}22`, color:colors.primaryDark, borderRadius:'100px', padding:'3px 10px', fontSize:'.7rem', fontWeight:'700' }}>
                                                            <span style={{ width:'5px', height:'5px', borderRadius:'50%', background:colors.primary, display:'inline-block' }}/>Inactivo
                                                        </span>
                                                    )}
                                                </td>
                                                {/* Acciones */}
                                                <td style={{ padding:'14px 18px', whiteSpace:'nowrap' }}>
                                                    <div style={{ display:'flex', gap:'6px' }}>
                                                        {permissionsModel?.can_read && canAccessRoute(`/userlevels/${level.id}/userlevelpermisos`, user?.role, permissions, disabledPaths) && (
                                                            <button className="nup-action-btn" onClick={()=>navigate(`/userlevels/${level.id}/userlevelpermisos`)} title="Ver permisos"
                                                                style={{ background:`${colors.accentDark}12`, color:colors.accentDark }}
                                                                onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background=colors.accentDark; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
                                                                onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background=`${colors.accentDark}12`; (e.currentTarget as HTMLButtonElement).style.color=colors.accentDark; }}>
                                                                <Shield size={13}/>
                                                            </button>
                                                        )}
                                                        {administrator && perm.can_update && (
                                                            <button className="nup-action-btn" onClick={()=>{ setSelectedLevel(level); setEditModalOpen(true); }} title="Editar"
                                                                style={{ background:`${colors.secondary}12`, color:colors.secondary }}
                                                                onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background=colors.secondary; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
                                                                onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background=`${colors.secondary}12`; (e.currentTarget as HTMLButtonElement).style.color=colors.secondary; }}>
                                                                <Edit size={13}/>
                                                            </button>
                                                        )}
                                                        {administrator && perm.can_delete && (
                                                            <button className="nup-action-btn" onClick={()=>{ setLevelToDelete(level); setDeleteModalOpen(true); }} title="Eliminar"
                                                                style={{ background:`${colors.primary}10`, color:colors.primary }}
                                                                onMouseEnter={e=>{ (e.currentTarget as HTMLButtonElement).style.background=colors.primary; (e.currentTarget as HTMLButtonElement).style.color='#fff'; }}
                                                                onMouseLeave={e=>{ (e.currentTarget as HTMLButtonElement).style.background=`${colors.primary}10`; (e.currentTarget as HTMLButtonElement).style.color=colors.primary; }}>
                                                                <Trash2 size={13}/>
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}

                                        {!loadError && sortedLevels.length===0 && (
                                            <tr>
                                                <td colSpan={5} style={{ padding:'60px 24px', textAlign:'center' }}>
                                                    <div style={{ display:'flex', flexDirection:'column', alignItems:'center', gap:'12px' }}>
                                                        <div style={{ width:'56px', height:'56px', borderRadius:'16px', background:`${colors.primary}10`, display:'flex', alignItems:'center', justifyContent:'center' }}>
                                                            <Shield size={24} color={colors.primary} style={{ opacity:.5 }}/>
                                                        </div>
                                                        <p style={{ fontSize:'1rem', fontWeight:'800', color:'#0f172a' }}>No hay niveles de usuario</p>
                                                        {perm.can_create && <p style={{ fontSize:'.82rem', color:'#94a3b8' }}>Agrega un nuevo nivel para comenzar</p>}
                                                        <div style={{ display:'flex', gap:'5px', marginTop:'4px' }}>
                                                            {[colors.primary,colors.secondary,colors.accentDark,colors.secondaryLight].map((c,i)=>(
                                                                <div key={i} style={{ height:'2px', width:i===0?'16px':'6px', borderRadius:'2px', background:c, opacity:.4 }}/>
                                                            ))}
                                                        </div>
                                                    </div>
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>

                            {sortedLevels.length>0 && (
                                <div style={{ borderTop:'1.5px solid #f1f5f9' }}>
                                    <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} itemsPerPage={itemsPerPage} totalItems={sortedLevels.length}/>
                                </div>
                            )}
                        </>
                    )}
                </div>
            </div>
        </>
    );
};

export default NivelUsuariosPage;
